import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, CalendarIcon, MapPinIcon, WrenchIcon, AlertTriangleIcon, FileTextIcon, TruckIcon, BarChart3Icon, PhoneIcon, RecycleIcon, SettingsIcon, ArrowDownIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Bilingual = {
  en: string;
  fr: string;
};
type Icon = typeof PhoneIcon;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 18
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.55,
    ease: EASE
  }
};
type Question = {
  id: string;
  icon: Icon;
  image?: {
    src: string;
    alt: Bilingual;
  };
  question: Bilingual;
  impact: Bilingual;
  detail: Bilingual;
};
const QUESTIONS: Question[] = [{
  id: 'sorting',
  icon: MessageCircleIcon,
  image: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/42283f42-4328-471a-814a-38b21dee0cd6.jpg',
    alt: {
      en: 'An empty yogurt container.',
      fr: 'Un contenant de yogourt vide.'
    }
  },
  question: {
    en: '“Where does this container go?”',
    fr: '« Où ça va, ce contenant ? »'
  },
  impact: {
    en: 'Contamination',
    fr: 'Contamination'
  },
  detail: {
    en: 'Wrong bin. Sorting error.',
    fr: 'Mauvais bac, erreur de tri.'
  }
}, {
  id: 'schedule',
  icon: CalendarIcon,
  question: {
    en: '“When is my collection?”',
    fr: '« Quand passent les collectes ? »'
  },
  impact: {
    en: 'Repetitive calls and requests',
    fr: 'Appels et demandes répétitifs'
  },
  detail: {
    en: 'Information is difficult to find.',
    fr: 'Information difficile à trouver.'
  }
}, {
  id: 'dropoff',
  icon: MapPinIcon,
  image: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/4ce35d03-58dc-467b-bd11-36566cf46158.jpg',
    alt: {
      en: 'Used batteries.',
      fr: 'Des piles usagées.'
    }
  },
  question: {
    en: '“Where can I drop off my batteries?”',
    fr: '« Où déposer mes piles ? »'
  },
  impact: {
    en: 'Underused service',
    fr: 'Service sous-utilisé'
  },
  detail: {
    en: 'Drop-off location unknown.',
    fr: 'Point de dépôt inconnu.'
  }
}, {
  id: 'bin',
  icon: WrenchIcon,
  image: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/2385c45a-2ab2-4a17-8143-b087930a8897.jpg',
    alt: {
      en: 'A municipal bin with a broken lid.',
      fr: 'Un bac municipal au couvercle brisé.'
    }
  },
  question: {
    en: '“My bin is broken.”',
    fr: '« Mon bac est brisé. »'
  },
  impact: {
    en: 'Administrative time',
    fr: 'Temps administratif'
  },
  detail: {
    en: 'Wrong channel. Manual processing.',
    fr: 'Mauvais canal, traitement manuel.'
  }
}];
const SOURCES: {
  id: string;
  icon: Icon;
  label: Bilingual;
}[] = [{
  id: 'rules',
  icon: FileTextIcon,
  label: {
    en: 'Sorting rules and materials',
    fr: 'Règles de tri et matières'
  }
}, {
  id: 'calendars',
  icon: CalendarIcon,
  label: {
    en: 'Collection calendars',
    fr: 'Calendriers de collecte'
  }
}, {
  id: 'special',
  icon: TruckIcon,
  label: {
    en: 'Special collections and bulky items',
    fr: 'Collectes spéciales et encombrants'
  }
}, {
  id: 'dropoffs',
  icon: MapPinIcon,
  label: {
    en: 'Drop-off locations and ecocentres',
    fr: 'Points de dépôt et écocentres'
  }
}, {
  id: 'trucks',
  icon: BarChart3Icon,
  label: {
    en: 'Truck and provider data',
    fr: 'Données des camions et fournisseurs'
  }
}];
const IMPACTS: {
  icon: Icon;
  title: Bilingual;
  desc: Bilingual;
}[] = [{
  icon: PhoneIcon,
  title: {
    en: 'More calls and requests',
    fr: 'Plus d’appels et de demandes'
  },
  desc: {
    en: 'Teams spend time answering the same questions.',
    fr: 'Des équipes mobilisées pour répondre aux mêmes questions.'
  }
}, {
  icon: RecycleIcon,
  title: {
    en: 'Material contamination',
    fr: 'Contamination des matières'
  },
  desc: {
    en: 'Incorrect sorting reduces the quality of recovered materials.',
    fr: 'Un mauvais tri réduit la qualité des matières récupérées.'
  }
}, {
  icon: MapPinIcon,
  title: {
    en: 'Underused services',
    fr: 'Services sous-utilisés'
  },
  desc: {
    en: 'Special collections, drop-off locations and recovery services remain difficult to discover.',
    fr: 'Les collectes spéciales, les écocentres et les points de dépôt restent difficiles à trouver.'
  }
}, {
  icon: SettingsIcon,
  title: {
    en: 'Less efficient operations',
    fr: 'Opérations moins efficaces'
  },
  desc: {
    en: 'Reports and requests must be found, sorted and routed.',
    fr: 'Signalements et demandes doivent être recherchés, triés et acheminés.'
  }
}, {
  icon: BarChart3Icon,
  title: {
    en: 'Too little data to drive improvement',
    fr: 'Peu de données pour s’améliorer'
  },
  desc: {
    en: 'Resident questions can reveal confusion and recurring pain points.',
    fr: 'Les questions des citoyens peuvent révéler les incompréhensions et irritants.'
  }
}];
const OUTCOMES: {
  icon: Icon;
  title: Bilingual;
  desc: Bilingual;
}[] = [{
  icon: ArrowDownIcon,
  title: {
    en: 'Fewer repetitive calls and requests',
    fr: 'Moins d’appels et de demandes répétitifs'
  },
  desc: {
    en: 'Residents find answers 24/7.',
    fr: 'Les citoyens trouvent leurs réponses 24/7.'
  }
}, {
  icon: RecycleIcon,
  title: {
    en: 'Better sorting decisions',
    fr: 'Un meilleur geste de tri'
  },
  desc: {
    en: 'Every material is directed to the right bin, collection or drop-off location.',
    fr: 'Chaque matière est orientée vers le bon bac, la bonne collecte ou le bon point de dépôt.'
  }
}, {
  icon: UsersIcon,
  title: {
    en: 'Greater use of services',
    fr: 'Des services plus utilisés'
  },
  desc: {
    en: 'Special collections, drop-off locations and services become easier to discover.',
    fr: 'Les collectes spéciales, les points de dépôt et les services deviennent plus faciles à trouver.'
  }
}, {
  icon: BarChart3Icon,
  title: {
    en: 'Greater operational capacity',
    fr: 'Une plus grande capacité opérationnelle'
  },
  desc: {
    en: 'Teams spend less time answering repetitive questions.',
    fr: 'Les équipes consacrent moins de temps aux questions répétitives.'
  }
}];

/** Desktop placement inside the collage (percent of the collage box). */
const QUESTION_POS: Record<string, {
  left: string;
  top: string;
  width: string;
  rotate: number;
}> = {
  sorting: {
    left: '6%',
    top: '0%',
    width: '44%',
    rotate: -1.4
  },
  schedule: {
    left: '56%',
    top: '2%',
    width: '38%',
    rotate: 1.2
  },
  dropoff: {
    left: '0%',
    top: '33%',
    width: '42%',
    rotate: 0.8
  },
  bin: {
    left: '58%',
    top: '35%',
    width: '42%',
    rotate: -0.9
  }
};
const SOURCE_POS: Record<string, {
  left: string;
  top: string;
}> = {
  rules: {
    left: '3%',
    top: '72%'
  },
  calendars: {
    left: '30%',
    top: '69%'
  },
  special: {
    left: '71%',
    top: '71%'
  },
  dropoffs: {
    left: '19%',
    top: '86%'
  },
  trucks: {
    left: '52%',
    top: '84%'
  }
};
function Knot({
  size = 230
}: {
  size?: number;
}) {
  return <div className="relative flex items-center justify-center" style={{
    width: size,
    height: size
  }} data-id="element-1600" data-mp="26kqa4rqpf4cc">
      <span className="absolute inset-[18%] rounded-full bg-[#4F46E5]/[0.05] blur-xl" aria-hidden="true" data-id="element-1601" data-mp="2extmfx48uuq9" />
      <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none" aria-hidden="true" data-id="element-1602" data-mp="2iyvkwbqc72r6">
        {[0, 24, 52, 78, 104, 132, 158].map((r, i) => <ellipse key={r} cx="100" cy="100" rx={78 - i % 3 * 8} ry={48 + i % 2 * 10} transform={`rotate(${r} 100 100)`} stroke={i % 2 === 0 ? '#A5A1EE' : '#D6D4F6'} strokeWidth="1" data-id="element-1603" data-mp="2swtab2jxn5ld" data-mpi={i} />)}
        <path d="M40 110 C 60 40, 150 40, 160 100 C 170 160, 70 170, 60 110 C 52 70, 130 60, 140 110" stroke="#C7C4F2" strokeWidth="1" data-id="element-1604" data-mp="36nevwz0rttmy" />
      </svg>
      <span className="relative w-14 h-14 rounded-full bg-white border border-[#E0DFF8] flex items-center justify-center shadow-[0_2px_10px_rgba(79,70,229,0.08)]" data-id="element-1605" data-mp="3gm46s7u92dtp">
        <AlertTriangleIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1606" data-mp="aoyiout13nb5" />
      </span>
    </div>;
}
export function CollectChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const renderQuestion = (q: Question) => {
    const QIcon = q.icon;
    return <div className="flex items-start gap-3 rounded-2xl border border-[#E7E8F0] bg-white p-3 shadow-[0_4px_18px_rgba(18,18,18,0.05)]" data-id="element-1607" data-mp="ep8puask4jrl">
        {q.image && <img src={q.image.src} alt={pick(q.image.alt)} loading="lazy" className="w-[84px] h-[76px] shrink-0 rounded-lg object-cover" data-id="element-1608" data-mp="3j6kvpw49e2mh" />}
        <div className="min-w-0 flex-1" data-id="element-1609" data-mp="3s56ensdn7jns">
          <div className="flex items-start gap-2.5" data-id="element-1610" data-mp="34gdpcerj0a25">
            <QIcon className="w-5 h-5 shrink-0 text-[#4F46E5] mt-0.5" strokeWidth={1.7} aria-hidden="true" data-id="element-1611" data-mp="34f2j88alyq7v" />
            <p className="font-serif text-[16px] text-[#121212] leading-snug" data-id="element-1612" data-mp="2zgr9dxkbcfpn">{pick(q.question)}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#FDF1F0] px-2.5 py-1 mt-2.5" data-id="element-1613" data-mp="1ex5cpdfkm3jn">
            <AlertTriangleIcon className="w-3.5 h-3.5 text-[#C2453A]" strokeWidth={1.8} aria-hidden="true" data-id="element-1614" data-mp="1jrt4bl5vfe2g" />
            <span className="font-sans text-[11.5px] font-medium text-[#B23A30] leading-tight" data-id="element-1615" data-mp="1axo6ghsbl2df">
              {pick(q.impact)}
            </span>
          </span>
          <p className="font-sans text-[12px] text-[#5B6478] leading-snug mt-2" data-id="element-1616" data-mp="1ynqwytgl609x">{pick(q.detail)}</p>
        </div>
      </div>;
  };
  const renderSource = (s: (typeof SOURCES)[number]) => {
    const SIcon = s.icon;
    return <div className="flex items-center gap-2.5 rounded-xl border border-[#EDEEF3] bg-white px-3 py-2" data-id="element-1617" data-mp="1orfflp252k8m">
        <SIcon className="w-4 h-4 shrink-0 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1618" data-mp="d9w83khizv49" />
        <span className="font-sans text-[11.5px] text-[#5B6478] leading-snug max-w-[140px]" data-id="element-1619" data-mp="i8zeb898s7dt">
          {pick(s.label)}
        </span>
      </div>;
  };
  return <div className="max-w-[1440px] mx-auto" data-id="element-1620" data-mp="3mu989br2cm26">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.78fr)_minmax(0,1.55fr)_minmax(0,0.95fr)] gap-12 xl:gap-8 items-start" data-id="element-1621" data-mp="3rskk1s0x56n1">
        {/* LEFT — the challenge */}
        <motion.div {...reveal} className="xl:pt-14" data-id="element-1622" data-mp="3d1tqofjohzht">
          <p className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-[#4F46E5]" data-id="element-1623" data-mp="3i1zbbbrj6lqe">
            {isFR ? 'Le défi' : 'The challenge'}
          </p>
          <h2 className="font-serif text-[2.6rem] lg:text-[3.1rem] text-[#121212] leading-[1.08] tracking-tight mt-6" data-id="element-1624" data-mp="384uvdtoa3mi1">
            {isFR ? <>
                Chaque mauvaise
                <span className="block" data-id="element-1625" data-mp="2z457dc59f0ev">réponse a un coût.</span>
              </> : <>
                Every wrong answer
                <span className="block" data-id="element-1626" data-mp="sf08lnj36wbz">has a cost.</span>
              </>}
          </h2>
          <ul className="mt-7 space-y-3 font-sans text-[16px] text-[#5B6478] leading-relaxed" data-id="element-1627" data-mp="xc9x90ynj3yr">
            {(isFR ? ['Une question sans réponse devient un appel.', 'Une matière mal orientée devient de la contamination.', 'Un service difficile à trouver est sous-utilisé.', 'Une demande mal dirigée mobilise inutilement vos équipes.'] : ['An unanswered question becomes a call.', 'A misplaced material becomes contamination.', 'A hard-to-find service goes underused.', 'A misdirected request unnecessarily consumes staff time.']).map(line => <li key={line} data-id="element-1628" data-mp="35k5bgatkrhq3">{line}</li>)}
          </ul>
        </motion.div>

        {/* CENTER — fragmented information (desktop collage) */}
        <motion.div {...reveal} transition={{
        duration: 0.55,
        delay: 0.08,
        ease: EASE
      }} data-id="element-1629" data-mp="1g7l4wx2g18uz">
          <div className="hidden xl:block relative h-[640px]" data-id="element-1630" data-mp="1b8gts0vyl120">
            {/* dotted, crossing paths to the knot */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" aria-hidden="true" data-id="element-1631" data-mp="24w4ttt20aqi3">
              {['M30 18 C 36 30, 44 36, 50 50', 'M74 20 C 64 30, 58 40, 50 50', 'M24 44 C 34 46, 40 54, 50 50', 'M78 48 C 66 44, 58 56, 50 50', 'M14 76 C 26 62, 40 66, 50 50', 'M38 73 C 44 64, 42 58, 50 50', 'M82 75 C 72 62, 60 66, 50 50', 'M30 89 C 34 70, 46 70, 50 50', 'M62 87 C 60 70, 54 64, 50 50', 'M52 8 C 46 24, 58 34, 50 50', 'M42 30 C 58 40, 42 62, 60 70'].map(d => <path key={d} d={d} stroke="#B9B6F0" strokeWidth="1" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" data-id="element-1632" data-mp="37izmbfr8sjkg" />)}
            </svg>

            <div className="absolute left-1/2 top-[50%] -translate-x-1/2 -translate-y-1/2" data-id="element-1633" data-mp="qymjp3u5u5c7">
              <Knot size={240} data-id="element-1634" data-mp="lzjbjaivvj00" />
            </div>

            {QUESTIONS.map(q => {
            const p = QUESTION_POS[q.id];
            return <div key={q.id} className="absolute z-10" style={{
              left: p.left,
              top: p.top,
              width: p.width,
              transform: `rotate(${p.rotate}deg)`
            }} data-id="element-1635" data-mp="1emdxkbvcmqfl">
                  {renderQuestion(q)}
                </div>;
          })}

            {SOURCES.map(s => {
            const p = SOURCE_POS[s.id];
            return <div key={s.id} className="absolute z-10" style={{
              left: p.left,
              top: p.top
            }} data-id="element-1636" data-mp="1xddpnly54gd3">
                  {renderSource(s)}
                </div>;
          })}
          </div>

          {/* Tablet / mobile: readable stack */}
          <div className="xl:hidden" data-id="element-1637" data-mp="1i2yw8jyv9ryp">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1638" data-mp="1i4082qnpiihh">
              {QUESTIONS.map(q => <div key={q.id} data-id="element-1639" data-mp="136zqvh3565x9">{renderQuestion(q)}</div>)}
            </div>
            <div className="flex justify-center my-6" data-id="element-1640" data-mp="185u6yb9q5gdo">
              <Knot size={150} data-id="element-1641" data-mp="186m0zhnob61u" />
            </div>
            <div className="flex flex-wrap justify-center gap-2.5" data-id="element-1642" data-mp="1vveuz8kqj3tt">
              {SOURCES.map(s => <div key={s.id} data-id="element-1643" data-mp="20s593qczbpcl">{renderSource(s)}</div>)}
            </div>
          </div>
        </motion.div>

        {/* RIGHT — impact on the organization */}
        <motion.div {...reveal} transition={{
        duration: 0.55,
        delay: 0.14,
        ease: EASE
      }} className="rounded-3xl border border-[#E7E8F0] bg-white px-6 py-7" data-id="element-1644" data-mp="1r2vfg62hlymd">
          <p className="font-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-1645" data-mp="2jk7rp796lfph">
            {isFR ? 'L’impact sur l’organisation' : 'Impact on the organization'}
          </p>
          <ul className="mt-5 divide-y divide-[#EDEEF3]" data-id="element-1646" data-mp="36nwedkq33cd">
            {IMPACTS.map(({
            icon: IIcon,
            title,
            desc
          }) => <li key={title.en} className="flex items-start gap-4 py-4" data-id="element-1647" data-mp="hth2qtc9kysf">
                <span className="w-11 h-11 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1648" data-mp="cvfi43z6wreu">
                  <IIcon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1649" data-mp="3lck20hj218mh" />
                </span>
                <span className="min-w-0" data-id="element-1650" data-mp="3ge8w2hvvri9j">
                  <span className="block font-sans text-[14.5px] font-medium text-[#121212] leading-snug" data-id="element-1651" data-mp="3ucoyvxqmgome">
                    {pick(title)}
                  </span>
                  <span className="block font-sans text-[13px] text-[#5B6478] leading-relaxed mt-1" data-id="element-1652" data-mp="3qcycs7c1iwo7">
                    {pick(desc)}
                  </span>
                </span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      {/* BOTTOM — the opportunity */}
      <motion.div {...reveal} className="mt-16 pt-12 border-t border-[#E7E8F0]" data-id="element-1653" data-mp="2bjteqzefncn">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1654" data-mp="76panchuwdue">
          <p className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-[#4F46E5]" data-id="element-1655" data-mp="c7dbepvw091t">
            {isFR ? 'Le potentiel' : 'The opportunity'}
          </p>
          <p className="font-serif text-[2rem] lg:text-[2.6rem] text-[#121212] leading-[1.15] mt-4" data-id="element-1656" data-mp="3foc05da9wlmm">
            {isFR ? 'Mieux informer, c’est déjà mieux gérer.' : 'Better information means better management.'}
          </p>
          <p className="font-sans text-[16px] text-[#5B6478] leading-relaxed mt-4" data-id="element-1657" data-mp="3oo8jczdw8saa">
            {isFR ? 'Collectes ATNA transforme chaque interaction en une occasion de mieux orienter les matières, de réduire la friction et d’augmenter la capacité de votre organisation.' : 'ATNA Waste Collection turns every interaction into an opportunity to better direct materials, reduce friction and increase organizational capacity.'}
          </p>
        </div>

        <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 xl:gap-0" data-id="element-1658" data-mp="2oglhf5x2iph2">
          {OUTCOMES.map(({
          icon: OIcon,
          title,
          desc
        }, i) => <li key={title.en} className={`flex items-start gap-4 xl:px-7 ${i > 0 ? 'xl:border-l xl:border-[#EDEEF3]' : 'xl:pl-0'}`} data-id="element-1659" data-mp="2kh2xodgzgkft" data-mpi={i}>
              <span className="w-12 h-12 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1660" data-mp="2aidhcp4qj81o">
                <OIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1661" data-mp="25o9byab4b82p" />
              </span>
              <span className="min-w-0" data-id="element-1662" data-mp="1wpnwio0y0965">
                <span className="block font-sans text-[15px] font-medium text-[#121212] leading-snug" data-id="element-1663" data-mp="1wqyx6ez3d0t1">
                  {pick(title)}
                </span>
                <span className="block font-sans text-[13.5px] text-[#5B6478] leading-relaxed mt-1.5" data-id="element-1664" data-mp="1mrhwkbyp5d3x">
                  {pick(desc)}
                </span>
              </span>
            </li>)}
        </ul>
      </motion.div>
    </div>;
}